import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";

const empty = { name: "", email: "", phone: "", course: "", message: "" };

export default function Contact() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("धन्यवाद! Your enquiry has been sent.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: Mail, label: "Email", value: settings?.email },
    { icon: Phone, label: "Phone", value: settings?.phone },
    { icon: MapPin, label: "Visit", value: settings?.address },
    { icon: Clock, label: "Hours", value: settings?.hours },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-40 max-w-[1400px] mx-auto px-6 md:px-10"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
        <div className="md:col-span-5">
          <span className="overline text-primary">04 — Begin</span>
          <h2 className="mt-6 font-display font-bold tracking-tight text-4xl md:text-6xl leading-[1.02]">
            Start your Hindi journey.
          </h2>
          <p className="mt-6 font-body text-muted-foreground max-w-md leading-relaxed">
            Send us an enquiry and our team will reach out with the right
            programme, schedule and fees for you.
          </p>

          <div className="mt-12 space-y-6">
            {info.map((it) => (
              <div
                key={it.label}
                data-testid={`contact-info-${it.label.toLowerCase()}`}
                className="flex items-start gap-4"
              >
                <it.icon size={18} className="mt-1 text-primary shrink-0" />
                <div>
                  <p className="overline text-muted-foreground">{it.label}</p>
                  <p className="font-body text-foreground">
                    {it.value || "—"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <motion.form
          data-testid="enquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="md:col-span-6 md:col-start-7 space-y-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Input
              data-testid="enquiry-name"
              placeholder="Your name"
              value={form.name}
              onChange={set("name")}
              className="h-12 rounded-none border-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-primary px-0"
            />
            <Input
              data-testid="enquiry-email"
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={set("email")}
              className="h-12 rounded-none border-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-primary px-0"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Input
              data-testid="enquiry-phone"
              placeholder="Phone number"
              value={form.phone}
              onChange={set("phone")}
              className="h-12 rounded-none border-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-primary px-0"
            />
            <Input
              data-testid="enquiry-course"
              placeholder="Interested course"
              value={form.course}
              onChange={set("course")}
              className="h-12 rounded-none border-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-primary px-0"
            />
          </div>
          <Textarea
            data-testid="enquiry-message"
            placeholder="Tell us about your goals..."
            value={form.message}
            onChange={set("message")}
            rows={4}
            className="rounded-none border-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-primary px-0 resize-none"
          />
          <Button
            data-testid="enquiry-submit"
            type="submit"
            disabled={loading}
            className="rounded-full bg-foreground text-background hover:bg-primary h-12 px-8 text-sm font-medium"
          >
            {loading ? "Sending..." : "Send enquiry →"}
          </Button>
        </motion.form>
      </div>
    </section>
  );
}
